import type { CSSProperties } from "react";
import { Image } from "@freightsimple/ui-core/Display/Image";

interface IconsLogoImageProps {
  src: string;
  /** Describe the mark — the bank, brand, or carrier name. */
  alt: string;
  /** Fixed size in px. */
  width?: number | undefined;
  height?: number | undefined;
  /** Ceiling in px, letting a mark of any aspect ratio sit in a shared box. */
  maxWidth?: number | undefined;
  maxHeight?: number | undefined;
  /** Horizontal gutter in px, for a mark that has to sit off its own edge. */
  marginLeft?: number | undefined;
  marginRight?: number | undefined;
}

/**
 * A third-party mark — a bank, card brand, or carrier logo.
 *
 * The sizing is ui-core's `Image` now that it takes a height and ceilings. All
 * this adds is the horizontal gutter, which only the generic card and bank
 * glyphs use: they are narrower than a real logo, and the gutter stops the text
 * after them shifting. Margin is the parent's business rather than the image's,
 * which is why it stayed here instead of going into `Image`.
 */
export function IconsLogoImage({
  src,
  alt,
  width,
  height,
  maxWidth,
  maxHeight,
  marginLeft,
  marginRight,
}: IconsLogoImageProps) {
  // `logo` carries the dark-mode pill and the hide-a-missing-file behaviour, so
  // neither lives here any more — see ui-core's Image.
  const image = (
    <Image
      logo
      src={src}
      alt={alt}
      width={width}
      height={height}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
    />
  );

  if (marginLeft === undefined && marginRight === undefined) return image;

  const gutter: CSSProperties = {
    ...(marginLeft !== undefined && { marginLeft }),
    ...(marginRight !== undefined && { marginRight }),
  };
  return <span style={gutter}>{image}</span>;
}
